import { Trans, useLingui } from "@lingui/react/macro";
import { Fragment, memo, type ReactNode } from "react";
import { EllipsisIcon, ListTodoIcon } from "lucide-react";
import { Button } from "../ui/button";
import {
  Menu,
  MenuItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator as MenuDivider,
  MenuTrigger,
} from "../ui/menu";

export const CompactComposerControlsMenu = memo(function CompactComposerControlsMenu(props: {
  activePlan: boolean;
  planSidebarLabel: string;
  planSidebarOpen: boolean;
  showDeepWorkToggle: boolean;
  deepWorkEnabled: boolean;
  onToggleDeepWork: () => void;
  onTogglePlanSidebar: () => void;
}) {
  const { t } = useLingui();
  const planSidebarLabel = props.planSidebarLabel.toLowerCase();
  const sections: Array<{ key: string; node: ReactNode }> = [];
  if (props.showDeepWorkToggle) {
    sections.push({
      key: "deep-work",
      node: (
        <>
          <div className="px-2 py-1.5 font-medium text-muted-foreground text-xs">
            <Trans>Deep Work</Trans>
          </div>
          <MenuRadioGroup
            value={props.deepWorkEnabled ? "on" : "off"}
            onValueChange={(value) => {
              if (!value || value === (props.deepWorkEnabled ? "on" : "off")) return;
              props.onToggleDeepWork();
            }}
          >
            <MenuRadioItem value="on">
              <Trans>On</Trans>
            </MenuRadioItem>
            <MenuRadioItem value="off">
              <Trans>Off</Trans>
            </MenuRadioItem>
          </MenuRadioGroup>
        </>
      ),
    });
  }
  if (props.activePlan) {
    sections.push({
      key: "plan-sidebar",
      node: (
        <MenuItem onClick={props.onTogglePlanSidebar}>
          <ListTodoIcon className="size-4 shrink-0" />
          {props.planSidebarOpen
            ? t`Hide ${planSidebarLabel} sidebar`
            : t`Show ${planSidebarLabel} sidebar`}
        </MenuItem>
      ),
    });
  }
  if (sections.length === 0) {
    return null;
  }
  return (
    <Menu>
      <MenuTrigger
        render={
          <Button
            size="sm"
            variant="ghost"
            className="shrink-0 px-2 text-muted-foreground/70 hover:text-foreground/80"
            aria-label={t`More composer controls`}
          />
        }
      >
        <EllipsisIcon aria-hidden="true" className="size-4" />
      </MenuTrigger>
      <MenuPopup align="start">
        {sections.map((section, index) => (
          <Fragment key={section.key}>
            {index > 0 ? <MenuDivider /> : null}
            {section.node}
          </Fragment>
        ))}
      </MenuPopup>
    </Menu>
  );
});
